import { IonButton, IonIcon, IonModal } from '@ionic/react';
import { settingsOutline } from 'ionicons/icons';
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { useMessages } from '../i18n/LanguageProvider';
import { SettingsPage } from './SettingsPage';

// DE: Ohne Anbieter (z. B. in einzelnen Bildschirmtests) tut das Öffnen nichts.
// EN: Without a provider (e.g. in single-screen tests) opening does nothing.
const OpenSettingsContext = createContext<() => void>(() => undefined);

/**
 * DE: Ein Einstellungsfenster für die ganze App; jeder Reiter öffnet es über das Zahnrad.
 * EN: One settings window for the whole app; every tab opens it through the gear.
 */
export function SettingsProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);
  const close = () => setIsOpen(false);
  return (
    <OpenSettingsContext.Provider value={open}>
      {children}
      <IonModal isOpen={isOpen} onDidDismiss={close}>
        {isOpen && <SettingsPage onClose={close} />}
      </IonModal>
    </OpenSettingsContext.Provider>
  );
}

/**
 * DE: Funktion, die die Einstellungen öffnet.
 * EN: Function that opens the settings.
 */
export function useOpenSettings(): () => void {
  return useContext(OpenSettingsContext);
}

/**
 * DE: Zahnrad für die Kopfzeile jedes Reiters.
 * EN: Gear for the header of every tab.
 */
export function SettingsButton() {
  const open = useOpenSettings();
  const { t } = useMessages();
  return (
    <IonButton className="app-round-button" fill="clear" onClick={open}>
      <IonIcon slot="icon-only" icon={settingsOutline} aria-hidden="true" />
      {/* DE: Ionic verschiebt aria-label in sein Schatten-DOM; sichtbar versteckter Text bleibt lesbar.
          EN: Ionic moves aria-label into its shadow DOM; visually hidden text stays readable. */}
      <span className="app-visually-hidden">{t.settings}</span>
    </IonButton>
  );
}
